import React from 'react';
import { SivaCompanyDetailDto, SivaContractDetailDto, SivaContractUpdateDto, SivaPhase } from 'src/api/v2';
import ValueField from 'src/components/ValueField/ValueField';
import useAdvisorOptions from '../../hooks/use-advisor-options';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TextArea, SelectOptionProps, DateInput } from '@innovationnorway/component-library';
import { Controller, useForm } from 'react-hook-form';
import useCompanyContract from '../../company-details/hooks/use-company-contract';
import useToggle from 'src/hooks/use-toggle';
import { Autocomplete } from 'src/components/Autocomplete/Autocomplete';
import { useOpenReportingYears } from '../../hooks/use-siva-open-reporting-years';
import { useCluster } from 'src/features/cluster';
import { Button, Heading, Icon } from '@innovationnorway/cl-react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';

type Props = {
  contract: SivaContractDetailDto;
  company: SivaCompanyDetailDto;
  selectedPhase: SivaPhase;
  onCancel: () => void;
};
const CompanyPhaseChangeForm: React.FC<Props> = ({ contract, company, selectedPhase, onCancel }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tSiva } = useTranslation('siva');

  const { advisorOptions } = useAdvisorOptions();
  const { cluster } = useCluster();

  const { currentPhase, updateSivaContractMutation } = useCompanyContract(contract.numericId);

  const [updateError, setUpdateError] = React.useState(false);
  const { activate, deactivate, isActive } = useToggle();

  const { handleSubmit, control } = useForm<SivaContractUpdateDto>({
    defaultValues: {
      ...contract,
      newPhase: {
        newPhase: undefined,
        fromDate: new Date(),
      },
    },
  });

  const { validateMinDate, validateMaxDate } = useOpenReportingYears(cluster?.id);
  const onSave = handleSubmit(async (formData) => {
    updateSivaContractMutation
      .mutateAsync({
        ...formData,
        newPhase: {
          newPhase: selectedPhase,
          fromDate: formData.newPhase?.fromDate,
        },
        advisorId: formData.advisorId,
        notes: formData.notes,
      })
      .then(() => {
        setUpdateError(false);
      })
      .catch(() => {
        setUpdateError(true);
      })
      .finally(() => {
        activate();
      });
  });

  return (
    <form onSubmit={onSave}>
      <Heading level={2}>Avtaleinformasjon</Heading>

      <ValueField label="Bedrift" value={company.name} />
      <Heading level={2}>Oppfølging</Heading>
      <Controller
        control={control}
        name="advisorId"
        render={({ field }) => (
          <Autocomplete<SelectOptionProps>
            label={tCommon('advisor')}
            name={field.name}
            items={advisorOptions}
            filter={(item, value: string): boolean => {
              const lowerCaseValue = value.toLowerCase();
              return item.text.toLowerCase().includes(lowerCaseValue) || false;
            }}
            onItemSelected={(advisor) => field.onChange(advisor.value)}
            itemLabel={(advisor) => `${advisor.text}`}
            renderItem={(advisor) => `${advisor.text}`}
            defaultValue={advisorOptions.find((x) => x.value === field.value)?.text}
          />
        )}
      />
      <p className="margin-top--0 margin-bottom--4">
        {tSiva('newCompany.contract.form.advisorId.link.text')}{' '}
        <Link to={`/actor-profile/settings/access`} target="_blank">
          <span className="margin-right--1">{tCommon('accessControl')}</span>
          <Icon prefix="fasr" iconName="external-link" />
        </Link>
      </p>

      <Controller
        control={control}
        name="newPhase.fromDate"
        rules={{
          required: tSiva('newCompany.contract.form.fromDate.required'),
          validate: {
            sameYear: (value) => {
              if (!value) {
                return true;
              }
              const date = new Date(value);
              if (date < validateMinDate || date > validateMaxDate) {
                return tSiva('newCompany.contract.form.fromDate.openReportingYear');
              }
              return true;
            },
          },
        }}
        render={({ field: { value, ...field }, fieldState }) => (
          <DateInput
            label={tSiva('company.details.contract.changePhase.fromDate')}
            className="margin-bottom--2"
            {...field}
            value={value ? new Date(value).toJSON().substring(0, 10) : undefined}
            errorMsg={fieldState.error?.message}
          />
        )}
      />

      <TextArea label="Notat" value={contract.notes} name="notes" />

      <div className="display--flex gap--1 justify-content--flex-start margin-top--7">
        <Button type="submit">{tCommon('save')}</Button>
        <Button variant="secondary" type="button" onClick={onCancel}>
          {tCommon('cancel')}
        </Button>
      </div>

      <DialogContainer
        show={isActive}
        onClose={updateError ? deactivate : onCancel}
        title={updateError ? tSiva('changePhase.error.title') : tCommon('confirmation')}
      >
        {updateError ? (
          <>
            <p>{tSiva('changePhase.error.text')}.</p>
            <Button type="button" onClick={deactivate}>
              {tCommon('close')}
            </Button>
          </>
        ) : (
          <>
            <p>{`${tSiva('changePhase.success.text')} ${tSiva(`phase.${currentPhase?.sivaPhase}` as any)},
              ${tCommon('to')} ${tSiva(`phase.${selectedPhase}` as any)}`}</p>
            <Button type="button" onClick={onCancel}>
              {tCommon('close')}
            </Button>
          </>
        )}
      </DialogContainer>
    </form>
  );
};
export default CompanyPhaseChangeForm;
